import { Box, Stack } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { ImageCanvas } from "components/common/ImageCanvas";
import { getCanvasAnnotationsFromROIs } from "features/canvas/utils";
import { AnswerBar } from "new-components/answerBar/AnswerBar";
import { CanvasAnnotation } from "types/imageAnnotations";
import { ImageQuery } from "types/imageQueries";
import { generateImageUrl } from "utils/generateImageUrl";
import { getClassNameAndConfidence } from "utils/imageQueriesUtils";

import { ClickOrigin, PageContext } from "./ImageQueryPreview";

interface ImageQueryViewProps {
  imageQuery: ImageQuery;
  id?: string;
  pageContext?: PageContext;
  classNames?: string;
  showConfidence?: boolean;
  showTimestamp?: boolean;
  titleSuffix?: string;
  openFocusModal?: (clickOrigin: ClickOrigin, event: React.MouseEvent) => void;
}

export function ImageQueryView(props: ImageQueryViewProps) {
  const {
    imageQuery,
    id,
    pageContext = PageContext.DetectorDetails,
    classNames,
    titleSuffix,
    showConfidence = false,
    showTimestamp = false,
    openFocusModal,
  } = props;

  const [answer, confidence] = useMemo(
    () => getClassNameAndConfidence(imageQuery, pageContext),
    [imageQuery, pageContext]
  );

  // annotations:
  const [annotations, setAnnotations] = useState<CanvasAnnotation[]>([]);
  useEffect(() => {
    const rois = imageQuery.current_best_prediction_rois
      ? getCanvasAnnotationsFromROIs(imageQuery.current_best_prediction_rois)
      : null;
    if (rois) {
      setAnnotations(rois);
    }
  }, [imageQuery, setAnnotations]);

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (openFocusModal) {
      e.stopPropagation();
      openFocusModal(ClickOrigin.Canvas, e);
    }
  };

  const handleAnswerBarClick = (e: React.MouseEvent) => {
    if (openFocusModal) {
      e.stopPropagation();
      openFocusModal(ClickOrigin.AnswerBar, e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Create a synthetic mouse event for keyboard navigation
    openFocusModal?.(ClickOrigin.Canvas, e as unknown as React.MouseEvent);
  };

  return (
    <Stack
      id={id}
      onKeyDown={handleKeyDown}
      sx={{
        height: "100%",
        width: "100%",
        cursor: "pointer",
      }}
    >
      <Box
        className={`w-full ${classNames || "h-[calc(100%-30px)]"} p-0 bg-zinc-200`}
        data-testid={`image-${imageQuery.iq_id}`}
        onClick={handleCanvasClick}
        sx={{
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            opacity: 0,
            transition: "opacity 0.2s ease",
            pointerEvents: "none",
          },
          "&:hover::after": {
            opacity: 1,
          },
        }}
      >
        <ImageCanvas
          imageUrl={generateImageUrl(imageQuery.iq_id)}
          annotations={annotations}
          style={{ cursor: "pointer" }}
          iqId={imageQuery.iq_id}
        />
      </Box>
      <Box component="button" onClick={handleAnswerBarClick}>
        <AnswerBar
          imageQuery={imageQuery}
          showTimestamp={showTimestamp}
          showConfidence={showConfidence}
          answer={answer}
          confidence={confidence}
          titleSuffix={titleSuffix}
        />
      </Box>
    </Stack>
  );
}
